import {
  Component,
  forwardRef,
  input,
  OnChanges,
  output,
  SimpleChanges,
  signal,
} from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatChipsModule } from "@angular/material/chips";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";

export interface MultiSelectOption<T = any> {
  value: T;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: "fh-multi-select-filter",
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
  ],
  templateUrl: "./multi-select-filter.component.html",
  styleUrls: ["./multi-select-filter.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectFilterComponent),
      multi: true,
    },
  ],
})
export class MultiSelectFilterComponent<T = any> implements ControlValueAccessor, OnChanges {
  // Inputs
  options = input.required<MultiSelectOption<T>[]>();
  label = input<string>("Select Multiple");
  placeholder = input<string>("Type to search...");
  selectedValues = input<T[]>([]);

  // Outputs
  selectionChange = output<T[]>();

  // Internal state
  searchText = signal<string>("");
  selectedOptions = signal<MultiSelectOption<T>[]>([]);
  filteredOptions = signal<MultiSelectOption<T>[]>([]);
  private selected = signal<Set<T>>(new Set());
  private _disabled = false;
  private _lastOptionsLength = 0;

  // Callbacks for ControlValueAccessor
  private onChange: (value: T[]) => void = () => {
    console.log("onChange");
  };
  private onTouched: () => void = () => {
    console.log("onTouched");
  };

  ngOnChanges(changes: SimpleChanges): void {
    // When options change (including first time), update filtered list
    if (changes.options) {
      const currentOptions = this.options();
      if (currentOptions.length !== this._lastOptionsLength) {
        this._lastOptionsLength = currentOptions.length;
      }
      this.updateSelectedOptions();
      this.updateFilteredOptions();
    }

    // When selectedValues input changes, sync internal state
    if (changes.selectedValues) {
      const values = this.selectedValues();
      this.selected.set(new Set(values));
      this.updateSelectedOptions();
      this.updateFilteredOptions();
    }
  }

  // ControlValueAccessor implementation
  writeValue(values: T[] | null): void {
    if (values && Array.isArray(values)) {
      this.selected.set(new Set(values));
    } else {
      this.selected.set(new Set());
    }
    this.updateSelectedOptions();
    this.updateFilteredOptions();
  }

  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  private updateSelectedOptions(): void {
    const selectedSet = this.selected();
    const opts = this.options().filter((opt) => selectedSet.has(opt.value));
    this.selectedOptions.set(opts);
  }

  private updateFilteredOptions(): void {
    const search = this.searchText().toLowerCase();
    const selectedSet = this.selected();
    const opts = this.options();

    // Filter out already selected options
    const unselected = opts.filter((opt) => !selectedSet.has(opt.value));

    if (!search) {
      this.filteredOptions.set(unselected);
      return;
    }

    this.filteredOptions.set(
      unselected.filter((option) => option.label.toLowerCase().includes(search))
    );
  }

  onSearchChange(value: string): void {
    console.log("onSearchChange", value);
    // Filtering happens via method call
    this.updateFilteredOptions();
  }

  onOptionSelected(value: T): void {
    const newSelected = new Set(this.selected());
    newSelected.add(value);
    this.selected.set(newSelected);
    this.searchText.set("");
    this.updateSelectedOptions();
    this.updateFilteredOptions();
    this.emitSelection();
  }

  removeOption(option: MultiSelectOption<T>): void {
    const newSelected = new Set(this.selected());
    newSelected.delete(option.value);
    this.selected.set(newSelected);
    this.updateSelectedOptions();
    this.updateFilteredOptions();
    this.emitSelection();
  }

  clearAll(): void {
    this.selected.set(new Set());
    this.searchText.set("");
    this.updateSelectedOptions();
    this.updateFilteredOptions();
    this.emitSelection();
  }

  private emitSelection(): void {
    const selectedArray = Array.from(this.selected());
    this.selectionChange.emit(selectedArray);
    this.onChange(selectedArray);
    this.onTouched();
  }

  get isDisabled(): boolean {
    return this._disabled;
  }
}
